// Sentry must be initialized before all other imports
import './instrument';

import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';

// Import auth store FIRST to ensure axios headers are set before any API calls
import './store/useAuthStore';

// Load i18n translation bundles before anything renders
import { loadTranslations } from './lib/i18n';
import en from './locales/en.json';
import fi from './locales/fi.json';
import sv from './locales/sv.json';

loadTranslations('en', en);
loadTranslations('fi', fi);
loadTranslations('sv', sv);

// Overlay published site-copy overrides (#662) on top of the baked bundles.
// Fire-and-forget: first paint uses baked copy; components re-render via the
// bundlesVersion bump if overrides arrive. Fails silently to baked copy.
import { applySiteCopyOverlay } from './lib/siteCopy';
void applySiteCopyOverlay();

import { initAudioDebug } from './lib/audioDebug';
import { initSupportErrorBuffer } from './lib/supportApi';

import App from './pages/App';
import './index.css';

initAudioDebug();
// Remember the last few JS errors so a support message can carry them.
initSupportErrorBuffer();

// Minimal, dependency-free fallback: ErrorBoundary may trigger before i18n or
// other app state is usable, so we keep the UI strictly inline and English.
const ErrorFallback = ({ resetError }: { resetError: () => void }) => (
    <div
        style={{
            padding: 40,
            textAlign: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            maxWidth: 520,
            margin: '80px auto',
        }}
    >
        <h1 style={{ fontSize: 22, marginBottom: 12 }}>Something went wrong</h1>
        <p style={{ color: '#555', marginBottom: 24 }}>
            We've been notified and are looking into it. Please try again.
        </p>
        <button
            onClick={resetError}
            style={{
                padding: '10px 20px',
                fontSize: 14,
                border: '1px solid #888',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
            }}
        >
            Try again
        </button>
    </div>
);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
    <React.StrictMode>
        <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={false}>
            <App />
        </Sentry.ErrorBoundary>
    </React.StrictMode>,
);
